/**
 * Araç kiralama refresh-resume wiring — see booking-flow/BookingFlowResumer.tsx and the
 * plan. Own file so `rerunSearch` can import `searchRentCars` without a circular import.
 * NOTE: `payment` runs AFTER `finalize` for rentCar.
 */
import type { ShoppingCart } from '../../api/types/shoppingCart';
import { searchRentCars } from '../../api/endpoints/rentCar';
import { bookingDraftKey } from '../../booking-flow/draftStorage';
import {
  cartIsPaid,
  clampStepIndex,
  compact,
  type BookingFlowPersistConfig,
  type ResumeAdapter,
} from '../../booking-flow/resume';
import type { RentCarFlowState } from './flowConfig';

/** Must match `rentCarFlowConfig.steps` order (asserted in resume.test.ts). */
const RENTCAR_STEP_ORDER = [
  'search',
  'select',
  'driver',
  'prebooking',
  'finalize',
  'payment',
  'document',
] as const;
const idx = (id: string) => RENTCAR_STEP_ORDER.indexOf(id as (typeof RENTCAR_STEP_ORDER)[number]);

const FLOOR = idx('driver');
const FINALIZE = idx('finalize');
const PAYMENT = idx('payment');
const DOCUMENT = idx('document');

const rentCarPartialize = (s: RentCarFlowState): Partial<RentCarFlowState> =>
  compact({
    searchRequest: s.searchRequest,
    selectedVehicle: s.selectedVehicle,
    driver: s.driver,
    payment: s.payment,
    shoppingCartId: s.shoppingCartId,
  });

export const rentCarPersistConfig: BookingFlowPersistConfig<RentCarFlowState> = {
  key: bookingDraftKey('rentCar'),
  version: 2,
  floorStepId: 'driver',
  searchResultStepId: 'select',
  partialize: rentCarPartialize,
  /** Onaya gönderilmiş taslağı YAZMA (uçuş/otel ile aynı). */
  shouldPersist: (s, stepIndex) => {
    if (s.sentForApproval) return false;
    if (stepIndex > 0) return true;
    return Object.keys(rentCarPartialize(s)).length > 0;
  },
};

export const rentCarResumeAdapter: ResumeAdapter<RentCarFlowState> = {
  getCartId: (draft) => draft.shoppingCartId,
  applyResumedCart: (cart: ShoppingCart) => ({ cart, shoppingCartId: cart.id }),
  resolveResumeStepId: (cart: ShoppingCart, savedStepId: string) => {
    if (cartIsPaid(cart)) return RENTCAR_STEP_ORDER[DOCUMENT];
    if (cart.isFinalized) return RENTCAR_STEP_ORDER[PAYMENT]; // don't re-run FinalizeShopping
    if (cart.isPriceChanged) return RENTCAR_STEP_ORDER[FLOOR];
    const saved = idx(savedStepId);
    return RENTCAR_STEP_ORDER[clampStepIndex(saved >= 0 ? saved : FLOOR, FLOOR, FINALIZE)];
  },
  rerunSearch: async (draft) => {
    if (!draft.searchRequest) return null;
    const searchResult = await searchRentCars(draft.searchRequest);
    return { searchResult };
  },
};
